// schema
import * as ISchema from "#webapp/src/__schema__";
// libraries
import { RoomOptionEnum } from "#webapp/src/components/event-scheduler";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { QueryRenderer, ReadyState } from "react-relay";
import { EventSchedulerTimeInput } from "./EventSchedulerTimeInput";
import * as s from "./EventSchedulerTimeInput.styles";
import { query } from "./EventSchedulerTimeInputRenderer.gql";
// other internals
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
import { handleReadyStateError } from "#webapp/src/util/relay.util";
import { EScheduleRange } from "#webapp/src/util/scheduler.util";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import { useRef } from "react";
import { Environment } from "relay-runtime";
import { setHighlightedSlot } from "../../state/actions/planner-highlight.actions";
import ErrorBoundary from "../error-boundary/ErrorBoundary";
interface Props {
  orgId: string;
  environment: Environment;
  invitees: Array<ISchema.IInviteeContextInput>;
  minutesDuration: number;
  timeZone: string;
  minSlot: ISchema.IDayTimeSlotInput;
  maxSlot: ISchema.IDayTimeSlotInput;
  includeRoom: RoomOptionEnum;
  roomsOverride: string[];
  limit: number;
  seenSlots: Array<ISchema.IDayTimeSlotInput>;
  traits: ISchema.EventTrait[];
  onChange?: (
    minutesDuration: number,
    includeRoom: RoomOptionEnum,
    range: EScheduleRange,
    specifiedDate: Date | null,
    uncrawledCalendarsCount: number,
    selectedSlot?: ISchema.ISchedulingSlot,
    specificRoomClicked?: boolean,
    conflictResolutionDefragId?: string,
  ) => void;
  range: EScheduleRange;
  specifiedDate: Date | null;
  selectedSlot?: ISchema.ISchedulingSlot;
  open?: boolean;
  persons: ISchema.IPerson[];
  timeError?: string;
  useAutoSchedule: boolean;
  isLoading: boolean;
  showCloseButton: boolean;
}
export const EventSchedulerTimeInputRenderer = ({
  orgId,
  invitees,
  minutesDuration,
  timeZone,
  minSlot,
  maxSlot,
  includeRoom,
  roomsOverride,
  limit,
  seenSlots,
  traits,
  environment,
  open,
  onChange,
  selectedSlot,
  range,
  specifiedDate,
  persons,
  timeError,
  useAutoSchedule,
  isLoading,
  showCloseButton,
}: Props) => {
  const cachedOrg = useRef<ISchema.IOrg | null>(null);
  const dispatch = useDispatch();
  const inputQuery = React.useMemo(
    () => ({
      invitees,
      minutesDuration,
      timeZone,
      minSlot,
      maxSlot,
      roomsOverride,
      limit,
      seenSlots,
      includeRoom: includeRoom !== RoomOptionEnum.WithoutRoom,
      traits,
    }),
    [
      includeRoom,
      invitees,
      limit,
      maxSlot,
      minSlot,
      minutesDuration,
      roomsOverride,
      seenSlots,
      timeZone,
      traits,
    ],
  );
  const reschedulerEvent = useSelector(
    (state: IReduxState) => state.feedScheduler.reschedulerEvent,
  );
  let externalEventId = "";
  if (reschedulerEvent) {
    externalEventId = reschedulerEvent.eventKey.externalEventId;
  }
  React.useEffect(() => {
    dispatch(setHighlightedSlot(selectedSlot ?? null));
  }, [selectedSlot?.timeRange?.startTime, selectedSlot?.timeRange?.endTime]);
  return (
    <QueryRenderer
      environment={environment}
      query={query}
      variables={{
        orgRelayId: orgId,
        query: inputQuery,
        rescheduler: !!reschedulerEvent,
        reschedulerExternalEventId: externalEventId,
        reschedulerTimeZone: timeZone,
        reschedulerRangeMin: minSlot,
        reschedulerRangeMax: maxSlot,
      }}
      cacheConfig={{}}
      render={(readyState: ReadyState) => {
        const freshOrg = readyState.props?.node;
        const loadingForTheFirstTime = !cachedOrg.current && !freshOrg;

        // check for error or show a loader for first load
        if (readyState.error) {
          handleReadyStateError(readyState.error, "EventSchedulerTimeInputRenderer");
          return <div>An error has occurred. Please try refreshing the page</div>;
        }

        if (loadingForTheFirstTime) {
          // loading for the first time
          return (
            <div
              style={
                open
                  ? {
                      ...s.editModeContainer,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }
                  : {
                      padding: "4px 0",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }
              }
            >
              <CircularProgress />
            </div>
          );
        }

        // cache an org so it doesn't flash on new loads
        let loading = false;
        if (freshOrg) {
          cachedOrg.current = freshOrg;
          loading = isLoading || false;
        } else {
          loading = isLoading || true;
        }

        const org = freshOrg || cachedOrg.current;
        const schedulingResponseErrorable =
          org.schedulingResponseErrorable || org.reschedulingResponseErrorable;

        return (
          <ErrorBoundary
            fallback={
              <div className="cw-body-base cw-py-2 cw-text-destructive">
                There was an issue fetching your suggested times.
              </div>
            }
          >
            <EventSchedulerTimeInput
              schedulingResponseErrorable={schedulingResponseErrorable}
              org={org}
              onChange={onChange}
              includeRoom={includeRoom}
              roomsOverride={roomsOverride}
              minutesDuration={minutesDuration}
              selectedSlot={selectedSlot}
              range={range}
              specifiedDate={specifiedDate}
              open={open}
              persons={persons}
              timeError={timeError}
              useAutoSchedule={useAutoSchedule}
              invitees={invitees}
              showCloseButton={showCloseButton}
              isLoading={loading}
            />
          </ErrorBoundary>
        );
      }}
    />
  );
};
